import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';
import {
  Search, Plus, Package, Minus, Edit2, Trash2,
  AlertTriangle, ChevronDown, Loader, X, Filter,
  ArrowRight, LayoutGrid, List
} from 'lucide-react';
import { useModal } from './AppModal';
import { useTheme } from './ThemeContext';
import { supabaseData } from './supabaseData';
import InventoryItemModal from './InventoryItemModal';
import StockWarningBadge from './StockWarningBadge';
import LocationPicker from './LocationPicker';
import TransferModal from './TransferModal';
import VisualLayoutView from './VisualLayoutView';

export default function InventoryPage({ companyId, onNavigate, userId, userRole }) {
  const { colors } = useTheme();
  const { showAlert, showConfirm, showSuccess, showError } = useModal();

  const isTech = userRole === 'field_tech' || userRole === 'member';

  const [inventoryEnabled, setInventoryEnabled] = useState(null); // null = loading, true/false
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [sortBy, setSortBy] = useState('name'); // name, quantity, category
  const [defaultLocation, setDefaultLocation] = useState(null);

  // Multi-location state
  const [inventoryMode, setInventoryMode] = useState('simple');
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'visual'

  // Truck picker for techs (multi-location)
  const [truckLocations, setTruckLocations] = useState([]);
  const [selectedTruckName, setSelectedTruckName] = useState('');

  // Modal state
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Guard against double-execution of loadInventory (React strict mode / double-render)
  const creatingDefaultLocationRef = useRef(false);

  // Quick adjust state
  const [adjustingItemId, setAdjustingItemId] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState(1);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (companyId) {
      loadCompanySettings();
    } else {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId && (selectedLocationId || inventoryMode === 'simple')) {
      loadInventory();
    }
  }, [companyId, selectedLocationId, inventoryMode]);

  const loadCompanySettings = async () => {
    try {
      const { data: company } = await supabase
        .from('companies')
        .select('inventory_mode')
        .eq('id', companyId)
        .single();

      if (company?.inventory_mode) {
        setInventoryMode(company.inventory_mode);
      }

      // Load truck/vehicle locations for the truck picker
      const { data: locations } = await supabase
        .from('locations')
        .select('id, name, location_type')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');

      setTruckLocations(locations || []);

      // For techs: try to restore last-used truck from localStorage, or find assigned vehicle
      if (userId && (locations || []).length > 1) {
        const savedTruckId = localStorage.getItem(`workvan_truck_${userId}`);
        if (savedTruckId && (locations || []).find(l => l.id === savedTruckId)) {
          setSelectedLocationId(savedTruckId);
          const loc = locations.find(l => l.id === savedTruckId);
          setSelectedTruckName(loc?.name || '');
          return;
        }

        // Try to find their assigned vehicle
        const { data: vehicle } = await supabase
          .from('vehicles')
          .select('location_id, name')
          .eq('company_id', companyId)
          .eq('assigned_tech_id', userId)
          .eq('is_active', true)
          .limit(1)
          .single();

        if (vehicle?.location_id) {
          setSelectedLocationId(vehicle.location_id);
          const loc = (locations || []).find(l => l.id === vehicle.location_id);
          setSelectedTruckName(loc?.name || vehicle.name || '');
          localStorage.setItem(`workvan_truck_${userId}`, vehicle.location_id);
        }
      }
    } catch (error) {
      console.error('Error loading company settings:', error);
    }
  };

  useEffect(() => {
    filterAndSortItems();
  }, [items, searchTerm, categoryFilter, showLowStockOnly, sortBy]);

  const loadInventory = async () => {
    try {
      setLoading(true);

      // Check if inventory is enabled
      const { data: company } = await supabaseData
        .from('companies')
        .select('inventory_enabled')
        .eq('id', companyId)
        .single();
      setInventoryEnabled(!!company?.inventory_enabled);
      if (!company?.inventory_enabled) {
        setLoading(false);
        return;
      }

      let locationId = selectedLocationId;

      // If no location selected yet, get default location
      if (!locationId) {
        const { data: defaultLoc } = await supabase
          .from('locations')
          .select('*')
          .eq('company_id', companyId)
          .eq('is_default', true)
          .single();

        if (defaultLoc) {
          setDefaultLocation(defaultLoc);
          locationId = defaultLoc.id;
          setSelectedLocationId(defaultLoc.id);
        } else {
          // No default location - create one automatically
          // Guard: prevent duplicate creation from concurrent calls (React double-render)
          if (creatingDefaultLocationRef.current) {
            setLoading(false);
            return;
          }
          creatingDefaultLocationRef.current = true;

          try {
            // Re-check in case another call already created one
            const { data: recheckLoc } = await supabase
              .from('locations')
              .select('*')
              .eq('company_id', companyId)
              .eq('is_default', true)
              .single();

            if (recheckLoc) {
              setDefaultLocation(recheckLoc);
              locationId = recheckLoc.id;
              setSelectedLocationId(recheckLoc.id);
            } else {
              const { data: newLoc, error: createError } = await supabase
                .from('locations')
                .insert({
                  company_id: companyId,
                  name: 'My Truck',
                  location_type: 'vehicle',
                  is_default: true
                })
                .select()
                .single();

              if (createError) {
                console.error('Error creating default location:', createError);
                setItems([]);
                setLoading(false);
                return;
              }

              setDefaultLocation(newLoc);
              locationId = newLoc.id;
              setSelectedLocationId(newLoc.id);
            }
          } finally {
            creatingDefaultLocationRef.current = false;
          }
        }
      }

      // Load inventory items with stock for this location
      const { data: inventoryItems, error } = await supabase
        .from('inventory_items')
        .select(`
          *,
          inventory_stock!inner(quantity_on_hand, quantity_reserved, quantity_available)
        `)
        .eq('company_id', companyId)
        .eq('is_active', true)
        .eq('inventory_stock.location_id', locationId)
        .order('name');

      if (error) {
        // Try without inner join if no stock records exist
        const { data: itemsOnly } = await supabase
          .from('inventory_items')
          .select('*')
          .eq('company_id', companyId)
          .eq('is_active', true)
          .order('name');

        // Get stock separately
        const { data: stockData } = await supabase
          .from('inventory_stock')
          .select('*')
          .eq('company_id', companyId)
          .eq('location_id', locationId);

        const stockMap = {};
        (stockData || []).forEach(s => {
          stockMap[s.inventory_item_id] = s;
        });

        const combined = (itemsOnly || []).map(item => ({
          ...item,
          inventory_stock: stockMap[item.id] ? [stockMap[item.id]] : [{
            quantity_on_hand: 0,
            quantity_reserved: 0,
            quantity_available: 0
          }]
        }));

        setItems(combined);

        // Extract unique categories from combined
        const uniqueCats = [...new Set(combined.map(i => i.category).filter(Boolean))];
        setCategories(uniqueCats);
      } else {
        setItems(inventoryItems || []);

        // Extract unique categories
        const uniqueCats = [...new Set((inventoryItems || []).map(i => i.category).filter(Boolean))];
        setCategories(uniqueCats);
      }

    } catch (error) {
      console.error('Error loading inventory:', error);
      showError('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortItems = () => {
    let filtered = [...items];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(term) ||
        item.part_number?.toLowerCase().includes(term) ||
        item.description?.toLowerCase().includes(term)
      );
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(item => item.category === categoryFilter);
    }

    // Low stock filter
    if (showLowStockOnly) {
      filtered = filtered.filter(item => {
        const stock = item.inventory_stock?.[0];
        const qty = stock?.quantity_on_hand || 0;
        return qty <= (item.reorder_point || 0);
      });
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'quantity':
          const qtyA = a.inventory_stock?.[0]?.quantity_on_hand || 0;
          const qtyB = b.inventory_stock?.[0]?.quantity_on_hand || 0;
          return qtyA - qtyB;
        case 'category':
          return (a.category || '').localeCompare(b.category || '');
        default:
          return 0;
      }
    });

    setFilteredItems(filtered);
  };

  const handleQuickAdjust = async (itemId, change) => {
    if (!selectedLocationId) return;
    // Guard against rapid clicks — skip if this item is already being adjusted
    if (adjustingItemId === itemId) return;

    setAdjustingItemId(itemId);
    try {
      const item = items.find(i => i.id === itemId);

      // Read current stock from database (not local state) to avoid stale reads
      const { data: existingStock } = await supabase
        .from('inventory_stock')
        .select('id, quantity_on_hand')
        .eq('inventory_item_id', itemId)
        .eq('location_id', selectedLocationId)
        .single();

      const currentQty = existingStock?.quantity_on_hand || 0;
      const newQty = Math.max(0, currentQty + change);

      if (existingStock) {
        // Update existing
        await supabase
          .from('inventory_stock')
          .update({
            quantity_on_hand: newQty,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingStock.id);
      } else {
        // Create new
        await supabase
          .from('inventory_stock')
          .insert({
            company_id: companyId,
            inventory_item_id: itemId,
            location_id: selectedLocationId,
            quantity_on_hand: newQty
          });
      }

      // Create transaction log
      await supabase
        .from('inventory_transactions')
        .insert({
          company_id: companyId,
          inventory_item_id: itemId,
          location_id: selectedLocationId,
          transaction_type: 'adjustment',
          quantity_change: change,
          quantity_before: currentQty,
          quantity_after: newQty,
          notes: `Quick adjust: ${change > 0 ? '+' : ''}${change}`
        });

      // Reload
      loadInventory();
      showSuccess(`Stock updated: ${item.name} now has ${newQty}`);

    } catch (error) {
      console.error('Error adjusting stock:', error);
      showError('Failed to adjust stock');
    } finally {
      setAdjustingItemId(null);
    }
  };

  const handleDeleteItem = async (item) => {
    const confirmed = await showConfirm(
      `Delete "${item.name}"? This will remove it from inventory.`,
      { confirmStyle: 'danger' }
    );

    if (!confirmed) return;

    try {
      await supabase
        .from('inventory_items')
        .update({ is_active: false })
        .eq('id', item.id);

      loadInventory();
      showSuccess('Item deleted');
    } catch (error) {
      showError('Failed to delete item');
    }
  };

  const handleItemSaved = () => {
    setShowItemModal(false);
    setEditingItem(null);
    loadInventory();
  };

  const getLowStockCount = () => {
    return items.filter(item => {
      const stock = item.inventory_stock?.[0];
      const qty = stock?.quantity_on_hand || 0;
      return qty <= (item.reorder_point || 0);
    }).length;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '50vh',
        color: colors.textMuted
      }}>
        <Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: '1rem' }}>Loading inventory...</span>
      </div>
    );
  }

  // Not enabled — show prompt
  if (inventoryEnabled === false) {
    return (
      <div style={{ padding: isMobile ? '1rem' : '2rem', maxWidth: '600px', margin: '0 auto' }}>
        <div style={{
          textAlign: 'center',
          padding: '3rem 2rem',
          background: colors.bgCard,
          borderRadius: '16px',
          border: `1px solid ${colors.border}`
        }}>
          <div style={{
            width: '72px', height: '72px', borderRadius: '16px',
            background: `${colors.accent}15`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 1.5rem'
          }}>
            <Package size={36} color={colors.accent} />
          </div>
          <h2 style={{ margin: '0 0 0.75rem', color: colors.text, fontSize: '1.5rem' }}>
            Inventory Management
          </h2>
          <p style={{ color: colors.textMuted, fontSize: '0.95rem', marginBottom: '1.5rem', lineHeight: 1.6 }}>
            Track parts and materials across your warehouse and trucks.
            Get low stock alerts, auto-deduct on job completion,
            and never show up to a job without the right parts.
          </p>
          <button
            onClick={() => onNavigate('settings', { section: 'inventory' })}
            style={{
              padding: '0.875rem 2rem',
              background: colors.accent,
              border: 'none',
              borderRadius: '10px',
              color: '#fff',
              fontSize: '1rem',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Package size={20} />
            Enable in Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '1rem' : '2rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, color: colors.text, fontSize: isMobile ? '1.5rem' : '2rem' }}>
            <Package size={isMobile ? 24 : 32} style={{ marginRight: '0.75rem', verticalAlign: 'middle' }} />
            Inventory
          </h1>
          {inventoryMode === 'simple' && truckLocations.length <= 1 && (
            <p style={{ margin: '0.5rem 0 0', color: colors.textMuted }}>
              {defaultLocation?.name || 'My Truck'} — {items.length} items
            </p>
          )}
          {/* Truck picker for when there are multiple locations but mode is still 'simple' */}
          {inventoryMode === 'simple' && truckLocations.length > 1 && (
            <div style={{ margin: '0.5rem 0 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <select
                value={selectedLocationId || ''}
                onChange={(e) => {
                  const locId = e.target.value;
                  setSelectedLocationId(locId);
                  const loc = truckLocations.find(l => l.id === locId);
                  setSelectedTruckName(loc?.name || '');
                  if (userId) localStorage.setItem(`workvan_truck_${userId}`, locId);
                }}
                style={{
                  padding: '0.4rem 0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  color: colors.text,
                  fontSize: '0.9rem'
                }}
              >
                {truckLocations.map(loc => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}{loc.location_type === 'vehicle' ? ' (truck)' : loc.location_type === 'warehouse' ? ' (warehouse)' : ''}
                  </option>
                ))}
              </select>
              <span style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                {items.length} items
              </span>
            </div>
          )}
        </div>

        {/* Multi-location controls */}
        {inventoryMode === 'multi_location' && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            flexWrap: 'wrap'
          }}>
            {/* View Mode Toggle */}
            <div style={{
              display: 'flex',
              background: colors.bgInput,
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              overflow: 'hidden'
            }}>
              <button
                onClick={() => setViewMode('list')}
                style={{
                  padding: '0.5rem 0.75rem',
                  background: viewMode === 'list' ? colors.accent : 'transparent',
                  border: 'none',
                  color: viewMode === 'list' ? '#fff' : colors.textMuted,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                <List size={16} />
              </button>
              <button
                onClick={() => setViewMode('visual')}
                style={{
                  padding: '0.5rem 0.75rem',
                  background: viewMode === 'visual' ? colors.accent : 'transparent',
                  border: 'none',
                  color: viewMode === 'visual' ? '#fff' : colors.textMuted,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                <LayoutGrid size={16} />
              </button>
            </div>

            {/* Location Picker */}
            <LocationPicker
              companyId={companyId}
              selectedLocationId={selectedLocationId}
              onChange={(locId) => setSelectedLocationId(locId)}
              style={{ minWidth: '200px' }}
            />

            {/* Transfer Button */}
            <button
              onClick={() => setShowTransferModal(true)}
              style={{
                padding: '0.6rem 1rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.accent,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: 'bold'
              }}
            >
              <ArrowRight size={18} />
              Transfer
            </button>
          </div>
        )}

        <button
          onClick={() => {
            setEditingItem(null);
            setShowItemModal(true);
          }}
          style={{
            padding: '0.75rem 1.5rem',
            background: colors.accent,
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            justifyContent: 'center'
          }}
        >
          <Plus size={20} />
          Add Item
        </button>
      </div>

      {/* Visual Layout View (Multi-location only) */}
      {inventoryMode === 'multi_location' && viewMode === 'visual' ? (
        <VisualLayoutView
          companyId={companyId}
          onViewLocation={(locId) => {
            setSelectedLocationId(locId);
            setViewMode('list');
          }}
          onTransfer={(locId) => {
            setSelectedLocationId(locId);
            setShowTransferModal(true);
          }}
        />
      ) : (
        <>
          {/* Low Stock Alert */}
          {getLowStockCount() > 0 && (
            <div style={{
              background: `${colors.warning}20`,
              border: `1px solid ${colors.warning}50`,
              borderRadius: '12px',
              padding: '1rem',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <AlertTriangle size={24} color={colors.warning} />
              <span style={{ color: colors.warning, fontWeight: 'bold' }}>
                {getLowStockCount()} item{getLowStockCount() !== 1 ? 's' : ''} low on stock
              </span>
              <button
                onClick={() => setShowLowStockOnly(!showLowStockOnly)}
                style={{
                  marginLeft: 'auto',
                  padding: '0.5rem 1rem',
                  background: showLowStockOnly ? colors.warning : 'transparent',
                  border: `1px solid ${colors.warning}`,
                  borderRadius: '6px',
                  color: showLowStockOnly ? '#fff' : colors.warning,
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                {showLowStockOnly ? 'Show All' : 'Show Low Stock'}
              </button>
            </div>
          )}

          {/* Search and Filters */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        {/* Search */}
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={18} style={{
            position: 'absolute',
            left: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: colors.textMuted
          }} />
          <input
            type="text"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem 0.75rem 2.75rem',
              background: colors.bgInput,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text,
              fontSize: '1rem'
            }}
          />
        </div>

        {/* Category Filter */}
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={{
            padding: '0.75rem 1rem',
            background: colors.bgInput,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            color: colors.text,
            minWidth: '150px'
          }}
        >
          <option value="all">All Categories</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>

        {/* Sort */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{
            padding: '0.75rem 1rem',
            background: colors.bgInput,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            color: colors.text,
            minWidth: '130px'
          }}
        >
          <option value="name">Sort: Name</option>
          <option value="quantity">Sort: Quantity</option>
          <option value="category">Sort: Category</option>
        </select>
      </div>

      {/* Items List */}
      {filteredItems.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: colors.textMuted
        }}>
          <Package size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
          <p style={{ margin: 0 }}>
            {items.length === 0 ? 'No inventory items yet' : 'No items match your filters'}
          </p>
          {items.length === 0 && (
            <button
              onClick={() => setShowItemModal(true)}
              style={{
                marginTop: '1rem',
                padding: '0.75rem 1.5rem',
                background: colors.accent,
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Add Your First Item
            </button>
          )}
        </div>
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem'
        }}>
          {filteredItems.map(item => {
            const stock = item.inventory_stock?.[0];
            const quantity = stock?.quantity_on_hand || 0;
            const isLowStock = quantity <= (item.reorder_point || 0);

            return (
              <div
                key={item.id}
                style={{
                  background: colors.bgCard,
                  borderRadius: '12px',
                  padding: '1rem 1.25rem',
                  border: `1px solid ${isLowStock ? colors.warning + '50' : colors.border}`,
                  display: 'flex',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: '1rem'
                }}
              >
                {/* Item Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.25rem'
                  }}>
                    <span style={{
                      color: colors.text,
                      fontWeight: 'bold',
                      fontSize: '1.05rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {item.name}
                    </span>
                    {isLowStock && <StockWarningBadge />}
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '1rem',
                    flexWrap: 'wrap',
                    color: colors.textMuted,
                    fontSize: '0.85rem'
                  }}>
                    {item.category && <span>{item.category}</span>}
                    {item.part_number && <span>PN: {item.part_number}</span>}
                    {item.unit_cost > 0 && <span>${item.unit_cost.toFixed(2)}/{item.unit_of_measure || 'ea'}</span>}
                  </div>
                </div>

                {/* Quantity Display & Controls */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  width: isMobile ? '100%' : 'auto',
                  justifyContent: isMobile ? 'space-between' : 'flex-end'
                }}>
                  {/* Quick Adjust Buttons */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleQuickAdjust(item.id, -1)}
                      disabled={adjustingItemId === item.id}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        color: colors.text,
                        cursor: adjustingItemId === item.id ? 'not-allowed' : 'pointer',
                        opacity: adjustingItemId === item.id ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Minus size={16} />
                    </button>

                    <div style={{
                      minWidth: '60px',
                      textAlign: 'center',
                      padding: '0.5rem',
                      background: isLowStock ? `${colors.warning}20` : colors.bgInput,
                      borderRadius: '8px',
                      border: `1px solid ${isLowStock ? colors.warning : colors.border}`
                    }}>
                      <div style={{
                        color: isLowStock ? colors.warning : colors.text,
                        fontWeight: 'bold',
                        fontSize: '1.1rem'
                      }}>
                        {quantity}
                      </div>
                    </div>

                    <button
                      onClick={() => handleQuickAdjust(item.id, 1)}
                      disabled={adjustingItemId === item.id}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        background: colors.accent,
                        border: 'none',
                        color: '#fff',
                        cursor: adjustingItemId === item.id ? 'not-allowed' : 'pointer',
                        opacity: adjustingItemId === item.id ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Plus size={16} />
                    </button>
                  </div>

                  {/* Edit/Delete */}
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => {
                        setEditingItem(item);
                        setShowItemModal(true);
                      }}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        color: colors.textMuted,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteItem(item)}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        color: colors.error,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
        </>
      )}

      {/* Item Modal */}
      {showItemModal && (
        <InventoryItemModal
          companyId={companyId}
          locationId={selectedLocationId}
          item={editingItem}
          onClose={() => {
            setShowItemModal(false);
            setEditingItem(null);
          }}
          onSave={handleItemSaved}
        />
      )}

      {/* Transfer Modal (Multi-location only) */}
      {showTransferModal && (
        <TransferModal
          companyId={companyId}
          defaultFromLocationId={selectedLocationId}
          onClose={() => setShowTransferModal(false)}
          onComplete={() => {
            setShowTransferModal(false);
            loadInventory();
          }}
        />
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
